'use client';

import { get } from 'lodash-es';
import Typography from '@ui/Typography';
import useShareModalStore from '@/components/store/shareModalSlice';
import Button from '@ui/Button';
import Input from '@ui/Input';
import React, { useState, useEffect } from 'react';
import { MdContentCopy, MdDone } from 'react-icons/md';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { getPostLink } from '@/utils/post.helper';
import { usePathname } from 'next/navigation';
import { joinUrl } from '@/utils/stringHelpers';
import getQualifiedImageUrl from '@/utils/imageUrlProcessor';
import { CampaignType } from '@/constants/postType.constants';

const iconSize = 65;
const smallIconSize = 40;
const windowSize = {
  width: 1024,
  height: 768,
};

function ProjectSuccessContent(props) {
  const { post, reward, type, isPost, isPostPublic, onContinue } = props;

  const badge = get(reward, 'badges.badge', null);
  const pointsEarned = get(reward, 'points.earned', 0);

  const pathName = usePathname();

  const postLink = getPostLink(post);

  const metadata = useShareModalStore((state) => state.metadata);

  const createPostShareLink = (url) => {
    let shareUrl = url || `${window.location.origin}${pathName}`;
    if (!shareUrl.startsWith(window.location.origin)) {
      shareUrl = joinUrl(window.location.origin, url);
    }
    return shareUrl;
  };

  const shareUrl = createPostShareLink(postLink);

  const title = metadata.title || 'Neki';
  const randomMessage = '';

  const description = randomMessage;

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 600);
    }
  }, [copied]);

  const handleLinkCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  };

  const isLessThanLargeScreen = useBreakpointHooks((bp) => bp.useSmaller('lg'));
  const isLessThanMediumScreen = useBreakpointHooks((bp) =>
    bp.useSmaller('md'),
  );

  const renderMessage = () => {
    switch (type) {
      case CampaignType.FundRaise:
        return (
          <Typography
            component="div"
            color="textDisabled"
            className="text-center font-medium"
            variant="h4">
            Project created successfully! You can now create a fundraiser
            campaign under this project. Just select the project while setting
            up your fundraiser and all the details will be pre-filled to save
            you time. Need help getting started? Read the step-by-step{' '}
            <Typography
              component="a"
              variant="h4"
              target="_blank"
              href="https://help.neki.io/en/article/how-to-create-a-fundraising-campaign-as-an-employee-of-a-company"
              className="font-medium text-text-hyperlink">
              guide
            </Typography>
            .
          </Typography>
        );
      case CampaignType.CollectGoods:
        return (
          <Typography
            component="div"
            color="textDisabled"
            className="text-center font-medium"
            variant="h4">
            Project created successfully! You can now create an in-kind donation
            campaign under this project. Just select the project while setting
            up your campaign and all the details will be pre-filled to save you
            time. Need help getting started? Read the step-by-step{' '}
            <Typography
              component="a"
              variant="h4"
              target="_blank"
              href="https://help.neki.io/en/article/how-to-create-an-in-kind-campaign-as-an-employee-of-a-company"
              className="font-medium text-text-hyperlink">
              guide
            </Typography>
            .
          </Typography>
        );
        break;
      default:
        break;
    }
  };

  if (isPost && !isPostPublic) {
    return (
      <div className="mt-4 flex flex-col items-center">
        <div className="left flex-1/2 flex flex-col items-center justify-center gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={
              badge
                ? getQualifiedImageUrl(badge?.imageUrl)
                : '/assets/images/star.png'
            }
            alt="badge"
            className="h-24 w-24"
          />
          <>
            <Typography
              component="div"
              className="text-center text-32"
              variant="h1">
              Congratulations!
            </Typography>
            {pointsEarned > 0 && (
              <Typography
                component="div"
                className="mx-[70px] text-center font-medium"
                variant="h4">
                You have earned <b>{pointsEarned} Nekies</b>{' '}
                {badge && (
                  <>
                    and a <b>{badge.name} Badge</b>
                  </>
                )}
              </Typography>
            )}
            {renderMessage()}
          </>
        </div>
        <Typography
          component="div"
          variant="hyperlink"
          className="mt-4 cursor-pointer text-center text-15"
          onClick={onContinue}>
          <u>Continue</u>
        </Typography>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col items-center">
      <div
        className={`flex w-full flex-1 ${'flex-col justify-center divide-y'}`}>
        <div className="flex-1/2 flex flex-col items-center justify-center gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={
              badge
                ? getQualifiedImageUrl(badge?.imageUrl)
                : '/assets/images/star.png'
            }
            alt="badge"
            className="h-24 w-24"
          />
          <Typography
            component="div"
            className="text-center text-32"
            variant="h1">
            Congratulations!
          </Typography>
          {pointsEarned > 0 && (
            <Typography
              component="div"
              className="mx-[70px] text-center font-medium"
              variant="h4">
              You have earned <b>{pointsEarned} Nekies</b>{' '}
              {badge && (
                <>
                  and a <b>{badge.name} Badge</b>
                </>
              )}
            </Typography>
          )}
          {renderMessage()}
        </div>
        <div
          className={`flex-1/2 flex flex-col items-center justify-center gap-12 ${
            isLessThanLargeScreen ? 'mt-6' : ''
          }`}>
          <Typography
            component="div"
            className={`text-center font-medium ${
              isLessThanLargeScreen ? 'mt-6' : ''
            }`}
            variant="h4">
            Don't be shy! Share your badge and encourage others to participate
            in your community!
          </Typography>
          <div
            className={`flex flex-wrap items-center justify-start ${
              isLessThanMediumScreen ? 'gap-4' : 'gap-6'
            }`}>
            <EmailShareButton
              url={shareUrl}
              subject={title}
              body={`${description} \n\n`}>
              <EmailIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                windowWidth={windowSize.width}
                windowHeight={windowSize.height}
                round
              />
            </EmailShareButton>

            <WhatsappShareButton
              url={shareUrl}
              title={title}
              separator=" "
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <WhatsappIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </WhatsappShareButton>

            <FacebookShareButton
              url={shareUrl}
              quote={title}
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <FacebookIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </FacebookShareButton>

            <TwitterShareButton
              url={shareUrl}
              title={title}
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <TwitterIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </TwitterShareButton>

            <LinkedinShareButton
              url={shareUrl}
              title={encodeURIComponent(title)}
              summary={encodeURIComponent(description)}
              source="Neki"
              windowWidth={windowSize.width}
              windowHeight={windowSize.height}
              //   onClick={handleClose}
            >
              <LinkedinIcon
                size={isLessThanMediumScreen ? smallIconSize : iconSize}
                round
              />
            </LinkedinShareButton>
          </div>

          <div className="flex w-full items-center justify-between gap-6">
            <div className="flex-1">
              <Input value={shareUrl} disabled />
            </div>
            <Button variant="filled" color="primary" onClick={handleLinkCopy}>
              {!copied ? (
                <MdContentCopy />
              ) : (
                <MdDone className="animate-pulse" />
              )}
            </Button>
          </div>
          {/* <Button className="w-full">Create a Post and Share</Button> */}
        </div>
      </div>
      <Typography
        component="div"
        variant="hyperlink"
        className="mt-4 cursor-pointer text-center text-15"
        onClick={onContinue}>
        <u>or Continue...</u>
      </Typography>
    </div>
  );
}

export default ProjectSuccessContent;
